import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/lib/auth-context";

// ─── Volume normalization ─────────────────────────────────────────────────────
/**
 * Parse `value` as a number, clamp to 0–100.
 * Never uses `|| 100` — 0% is intentionally silent and must stay 0.
 */
export function normalizeNotificationVolume(value: unknown, fallback = 100): number {
  const n = Number(value);
  if (!isFinite(n)) return Math.min(100, Math.max(0, fallback));
  return Math.min(100, Math.max(0, n));
}

// ─── Shared AudioContext ──────────────────────────────────────────────────────
let _sharedCtx: AudioContext | null = null;

function getOrCreateCtx(): AudioContext | null {
  if (!_sharedCtx || _sharedCtx.state === "closed") {
    try { _sharedCtx = new AudioContext(); } catch { return null; }
  }
  return _sharedCtx;
}

export function unlockAudioContext() {
  const ctx = getOrCreateCtx();
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
}

function playBeepWithVolume(volume: number) {
  const ctx = getOrCreateCtx();
  if (!ctx || ctx.state === "suspended") return;
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    const vol = Math.min(1, Math.max(0, volume / 100)) * 0.3;
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
    osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch {} };
  } catch {}
}

export function playNotificationBeep(volume = 100) {
  playBeepWithVolume(normalizeNotificationVolume(volume));
}

export function playCustomOrBeep(volume: number, audioUrl?: string | null) {
  const vol = normalizeNotificationVolume(volume);
  if (vol === 0) return; // intentionally silent
  if (audioUrl) {
    try {
      const audio = new Audio(audioUrl);
      audio.volume = vol / 100;
      audio.onerror = () => playBeepWithVolume(vol);
      audio.play().catch(() => playBeepWithVolume(vol));
    } catch {
      playBeepWithVolume(vol);
    }
  } else {
    playBeepWithVolume(vol);
  }
}

// ─── Personal mute (conversations page only) ──────────────────────────────────
// localStorage key: snd_conv_muted_{userId}
// "true" = muted; absent/other = not muted.
// BroadcastChannel "conv-sound-mute" synchronises the toggle across open tabs
// for the same user without requiring a page reload.
const MUTE_BC_CHANNEL = "conv-sound-mute";
const mutedKey = (userId: string) => `snd_conv_muted_${userId}`;

/**
 * Returns `{ enabled, toggle }` for the per-user personal mute preference.
 *
 * `enabled = true` means the user has NOT personally muted sound.
 * Tenant-level authority is enforced by the centralised sound controller in
 * App.tsx — this hook only controls the personal mute toggle shown on the
 * Conversations page header.
 *
 * Migration: converts the old `snd_conv_enabled_{userId}` / legacy
 * `notification_sound_conversations` keys to the new mute key on first use.
 */
export function useNotificationSound(screen: string) {
  const { user } = useAuth();
  const userId = (user as any)?.id || "anon";
  const mk = mutedKey(userId);

  const [muted, setMuted] = useState<boolean>(() => {
    if (screen !== "conversations") return false;
    // Migrate old snd_conv_enabled_{userId} key (false → muted, true → not muted)
    const legacyEnabled = localStorage.getItem(`snd_conv_enabled_${userId}`);
    if (legacyEnabled !== null) {
      const wasMuted = legacyEnabled === "false";
      localStorage.removeItem(`snd_conv_enabled_${userId}`);
      if (wasMuted) { localStorage.setItem(mk, "true"); return true; }
      return false;
    }
    // Migrate even older notification_sound_conversations key
    const legacyOld = localStorage.getItem("notification_sound_conversations");
    if (legacyOld === "false") return true;
    return localStorage.getItem(mk) === "true";
  });

  // BroadcastChannel + storage event: keep all open tabs in sync
  useEffect(() => {
    if (screen !== "conversations") return;
    let bc: BroadcastChannel | null = null;
    try { bc = new BroadcastChannel(MUTE_BC_CHANNEL); } catch {}
    const onBcMsg = (e: MessageEvent) => {
      if (e.data?.userId === userId && typeof e.data?.muted === "boolean") {
        setMuted(e.data.muted);
      }
    };
    if (bc) bc.onmessage = onBcMsg;
    const onStorage = (e: StorageEvent) => {
      if (e.key === mk) setMuted(e.newValue === "true");
    };
    window.addEventListener("storage", onStorage);
    return () => {
      bc?.close();
      window.removeEventListener("storage", onStorage);
    };
  }, [userId, mk, screen]);

  const toggle = useCallback(() => {
    setMuted(prev => {
      const next = !prev;
      localStorage.setItem(mk, String(next));
      try {
        const bc = new BroadcastChannel(MUTE_BC_CHANNEL);
        bc.postMessage({ userId, muted: next });
        bc.close();
      } catch {}
      return next;
    });
  }, [mk, userId]);

  return { enabled: !muted, toggle, playSound: playNotificationBeep };
}

// ─── Per-screen incoming-message sound alert ─────────────────────────────────
/**
 * Plays a beep when a new incoming message appears in `messages` for surfaces
 * that are not covered by the centralised Pusher-based sound controller in
 * App.tsx (tickets, team chat). Count-based; resets when `contextKey` changes.
 */
export function useMessageSoundAlert(
  screen: string,
  messages: any[] | undefined,
  enabled: boolean,
  currentUserId?: string,
  contextKey?: string | null
) {
  const prevCountRef = useRef<number>(0);
  const initializedRef = useRef(false);
  const prevContextRef = useRef<string | null | undefined>(contextKey);

  useEffect(() => {
    if (prevContextRef.current !== contextKey) {
      prevContextRef.current = contextKey;
      initializedRef.current = false;
    }
  }, [contextKey]);

  useEffect(() => {
    if (!messages) return;
    const count = messages.length;

    if (!initializedRef.current) {
      prevCountRef.current = count;
      initializedRef.current = true;
      return;
    }

    if (count > prevCountRef.current && enabled) {
      const newMessages = messages.slice(prevCountRef.current);
      const hasIncoming = newMessages.some((m: any) => {
        if (currentUserId && m.senderId === currentUserId) return false;
        if (currentUserId && m.userId === currentUserId) return false;
        if (currentUserId && m.senderUserId === currentUserId) return false;
        if (m.direction === "outbound") return false;
        return true;
      });
      if (hasIncoming) {
        playNotificationBeep();
      }
    }
    prevCountRef.current = count;
  }, [messages?.length, enabled, currentUserId]);
}
